import { useId, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '@dental-staffing-platform/auth';
import {
  supportCaseActionInput,
  supportOperationInput,
  supportPriority,
  type SupportCaseActionInput,
  type SupportOperationInput,
} from '@dental-staffing-platform/shared';
import {
  Badge,
  Button,
  Card,
  Field,
  PageHeader,
  Pagination,
  Row,
  Select,
  SkeletonList,
  Stack,
  Textarea,
} from '@dental-staffing-platform/ui';
import { supportOperationsApi } from '../lib/support-operations-client.js';
import { AdminApiError } from '../lib/admin-client.js';
import { useAsync } from '../lib/useAsync.js';
import { humanize } from '../lib/format.js';

type Pending =
  | { kind: 'operations'; input: SupportOperationInput }
  | { kind: 'shared'; input: SupportCaseActionInput };
function retained(key: string): Pending | null {
  try {
    const value: unknown = JSON.parse(sessionStorage.getItem(key) ?? 'null');
    if (!value || typeof value !== 'object' || !('kind' in value) || !('input' in value))
      return null;
    if (value.kind === 'operations') {
      const parsed = supportOperationInput.safeParse(value.input);
      return parsed.success ? { kind: 'operations', input: parsed.data } : null;
    }
    if (value.kind === 'shared') {
      const parsed = supportCaseActionInput.safeParse(value.input);
      return parsed.success ? { kind: 'shared', input: parsed.data } : null;
    }
  } catch {
    /* Invalid or unavailable storage never supplies a mutation. */
  }
  return null;
}
export function SupportCaseDetail() {
  const { caseId } = useParams();
  const { user } = useAuth();
  if (!caseId) return <p role="alert">Choose a case from the support queue.</p>;
  const storageKey = `support-operator:${user?.organizationId ?? ''}:${user?.id ?? ''}:${caseId}`;
  return (
    <OperatorCase
      key={storageKey}
      caseId={caseId}
      storageKey={storageKey}
      userId={user?.id ?? ''}
    />
  );
}
function OperatorCase({
  caseId,
  storageKey,
  userId,
}: {
  caseId: string;
  storageKey: string;
  userId: string;
}) {
  const formId = useId();
  const [page, setPage] = useState(1),
    [internalPage, setInternalPage] = useState(1);
  const state = useAsync(
    () => supportOperationsApi.detail(caseId, page, internalPage),
    [caseId, page, internalPage]
  );
  const [pending, setPending] = useState<Pending | null>(() => retained(storageKey));
  const [busy, setBusy] = useState(false),
    [error, setError] = useState<string | null>(null),
    [notice, setNotice] = useState<string | null>(null);
  const [internalBody, setInternalBody] = useState(''),
    [sharedBody, setSharedBody] = useState(''),
    [priority, setPriority] = useState('');
  const [action, setAction] = useState<SupportCaseActionInput['action']>('comment'),
    [acknowledged, setAcknowledged] = useState(false),
    [discardAcknowledged, setDiscardAcknowledged] = useState(false);
  const data = !state.loading && !state.error ? state.data : null;
  const record = data?.case;
  const locked = busy || !!pending || !data;
  const send = async (request: Pending) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      // Persist before sending. An uncertain response is retried using the exact payload.
      sessionStorage.setItem(storageKey, JSON.stringify(request));
      setPending(request);
      if (request.kind === 'operations') await supportOperationsApi.operate(caseId, request.input);
      else await supportOperationsApi.reply(caseId, request.input);
      sessionStorage.removeItem(storageKey);
      setPending(null);
      if (request.kind === 'shared') {
        setSharedBody('');
        setAcknowledged(false);
      } else if (request.input.action === 'internal_note') {
        setInternalBody('');
      }
      setDiscardAcknowledged(false);
      setNotice(
        request.kind === 'shared' ? 'Shared case update saved.' : 'Private operator update saved.'
      );
      state.reload();
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : 'The save could not be confirmed. Retry the same request.'
      );
      if (cause instanceof AdminApiError && [400, 403, 404, 413, 422].includes(cause.status)) {
        sessionStorage.removeItem(storageKey);
        setPending(null);
      }
    } finally {
      setBusy(false);
    }
  };
  const operate = (
    input: Omit<SupportOperationInput, 'operationsRevision' | 'clientRequestId'>
  ) => {
    if (!data) return;
    void send({
      kind: 'operations',
      input: {
        ...input,
        operationsRevision: data.operations.operationsRevision,
        clientRequestId: crypto.randomUUID(),
      },
    });
  };
  const actions = record
    ? (
        [
          ['comment', 'Send shared reply', record.canComment],
          ['start_review', 'Start review', record.canStartReview],
          ['resolve', 'Resolve case', record.canResolve],
          ['close', 'Close case', record.canClose],
          ['reopen', 'Reopen case', record.canReopen],
        ] as const
      ).filter(([, , allowed]) => allowed)
    : [];
  const selected = actions.find(([kind]) => kind === action)?.[0] ?? actions[0]?.[0];
  const reply = (e: FormEvent) => {
    e.preventDefault();
    if (!record || !selected || !acknowledged) return;
    void send({
      kind: 'shared',
      input: {
        action: selected,
        body: sharedBody,
        revision: record.revision,
        clientRequestId: crypto.randomUUID(),
      },
    });
  };
  return (
    <Stack gap={20} style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
      <Link to="/support">Back to support queue</Link>
      <PageHeader
        title={record?.subject ?? 'Support case'}
        subtitle={
          record
            ? `${record.reference} · ${humanize(record.category)}`
            : 'Read the shared booking case and operator history.'
        }
        actions={
          <Button disabled={busy} onClick={state.reload}>
            Refresh case
          </Button>
        }
      />
      {notice && <p role="status">{notice}</p>}
      {error && <p role="alert">{error}</p>}
      {pending && (
        <Card>
          <Stack gap={12}>
            <h2>Retained support update</h2>
            <p>
              The previous save is not confirmed. Other changes stay locked until this request is
              confirmed or you review and discard it.
            </p>
            <p>
              {pending.kind === 'shared'
                ? 'Shared booking conversation'
                : 'Private operator history'}{' '}
              · {humanize(pending.input.action)}
            </p>
            {pending.input.body && (
              <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {pending.input.body}
              </p>
            )}
            <Button disabled={busy || state.loading} onClick={() => void send(pending)}>
              {busy ? 'Saving…' : 'Retry same support update'}
            </Button>
            <label style={{ display: 'flex', gap: 10, alignItems: 'start' }}>
              <input
                type="checkbox"
                disabled={busy}
                checked={discardAcknowledged}
                onChange={(e) => setDiscardAcknowledged(e.target.checked)}
              />
              <span>
                I checked both histories and understand the previous save may have succeeded.
              </span>
            </label>
            <Button
              variant="secondary"
              disabled={busy || !discardAcknowledged}
              onClick={() => {
                sessionStorage.removeItem(storageKey);
                setPending(null);
                setDiscardAcknowledged(false);
                setError(null);
                state.reload();
              }}
            >
              Discard retained request
            </Button>
          </Stack>
        </Card>
      )}
      {state.loading ? (
        <SkeletonList count={3} />
      ) : state.error || !data ? (
        <div role="alert">
          <p>{state.error ?? 'Case could not be loaded.'}</p>
          <Button onClick={state.reload}>Retry case</Button>
        </div>
      ) : (
        <>
          <Card>
            <Stack gap={12}>
              <Row style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <h2>Booking issue</h2>
                <Badge>{humanize(data.case.status)}</Badge>
              </Row>
              <p>
                {data.case.booking.locationName} · {data.case.booking.professionalName}
              </p>
              <p className="dsp-muted">
                {new Date(data.case.booking.startsAt).toLocaleString()} to{' '}
                {new Date(data.case.booking.endsAt).toLocaleString()} · {data.case.booking.timezone}
              </p>
              <p style={{ overflowWrap: 'anywhere' }}>Booking reference: {data.case.bookingId}</p>
              <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {data.case.description}
              </p>
              <p className="dsp-muted">
                Opened by {data.case.createdByName} ·{' '}
                {new Date(data.case.createdAt).toLocaleString()}
              </p>
              {data.case.resolution && (
                <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  Recorded resolution: {data.case.resolution}
                </p>
              )}
            </Stack>
          </Card>
          <Card>
            <Stack gap={12}>
              <h2>Operator ownership and priority</h2>
              <p>Owner: {data.operations.assignedName ?? 'Unassigned'}</p>
              <Row style={{ flexWrap: 'wrap' }}>
                <Button
                  disabled={
                    locked ||
                    (!!data.operations.assignedUserId &&
                      data.operations.assignedUserId !== userId) ||
                    data.operations.assignedUserId === userId
                  }
                  onClick={() => operate({ action: 'claim', body: '' })}
                >
                  Claim case
                </Button>
                <Button
                  variant="secondary"
                  disabled={locked || !data.operations.assignedUserId}
                  onClick={() => operate({ action: 'unassign', body: '' })}
                >
                  Unassign case
                </Button>
              </Row>
              <p className="dsp-muted">
                Operators claim work for themselves using their verified account. Unassigning
                preserves the previous owner in private history.
              </p>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  operate({
                    action: 'priority',
                    priority: supportPriority.parse(priority || data.operations.priority),
                    body: '',
                  });
                }}
              >
                <Row style={{ alignItems: 'end', flexWrap: 'wrap' }}>
                  <Field label="Case priority" htmlFor={`${formId}-priority`}>
                    <Select
                      id={`${formId}-priority`}
                      disabled={locked}
                      value={priority || data.operations.priority}
                      onChange={(e) => setPriority(e.target.value)}
                    >
                      {supportPriority.options.map((value) => (
                        <option key={value} value={value}>
                          {humanize(value)}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Button
                    disabled={locked || !priority || priority === data.operations.priority}
                    type="submit"
                  >
                    Save priority
                  </Button>
                </Row>
              </form>
              <p className="dsp-muted">
                Priority is for triage. It does not promise emergency coverage or a response
                deadline.
              </p>
            </Stack>
          </Card>
          <Card>
            <Stack gap={12}>
              <h2>Shared booking conversation</h2>
              <p className="dsp-muted">
                Visible to the booking parties and organization support administrators.
              </p>
              {data.events.items.map((event) => (
                <article
                  key={event.id}
                  style={{ borderTop: '1px solid var(--dsp-border)', paddingTop: 12 }}
                >
                  <h3 style={{ fontSize: '1rem' }}>{humanize(event.kind)}</h3>
                  <p className="dsp-muted">
                    {event.actorName} · {humanize(event.actorSide)} ·{' '}
                    {new Date(event.recordedAt).toLocaleString()} · Revision {event.revision}
                  </p>
                  <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{event.body}</p>
                </article>
              ))}
              <Pagination
                page={data.events.page}
                pageSize={data.events.pageSize}
                total={data.events.total}
                totalPages={data.events.totalPages}
                onPageChange={setPage}
                itemLabel="shared updates"
              />
              {selected && (
                <form onSubmit={reply}>
                  <Stack gap={12}>
                    <Field label="Shared case action" htmlFor={`${formId}-action`}>
                      <Select
                        id={`${formId}-action`}
                        value={selected}
                        disabled={locked}
                        onChange={(e) =>
                          setAction(supportCaseActionInput.shape.action.parse(e.target.value))
                        }
                      >
                        {actions.map(([kind, label]) => (
                          <option key={kind} value={kind}>
                            {label}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field label="Shared reply or decision reason" htmlFor={`${formId}-shared`}>
                      <Textarea
                        id={`${formId}-shared`}
                        required
                        minLength={3}
                        maxLength={5000}
                        rows={5}
                        disabled={locked}
                        value={sharedBody}
                        onChange={(e) => setSharedBody(e.target.value)}
                      />
                    </Field>
                    <label style={{ display: 'flex', gap: 10, alignItems: 'start' }}>
                      <input
                        type="checkbox"
                        disabled={locked}
                        checked={acknowledged}
                        onChange={(e) => setAcknowledged(e.target.checked)}
                      />
                      <span>I reviewed this update for sharing with the booking parties.</span>
                    </label>
                    <p className="dsp-muted">
                      Resolving or closing a case records the discussion outcome. It does not change
                      approved hours, booking terms, charges or payouts.
                    </p>
                    <Button
                      type="submit"
                      disabled={locked || !acknowledged || sharedBody.trim().length < 3}
                    >
                      Save shared update
                    </Button>
                  </Stack>
                </form>
              )}
            </Stack>
          </Card>
          <Card>
            <Stack gap={12}>
              <h2>Private operator notes and history</h2>
              <p>
                Only organization administrators can read this section. These notes never appear in
                the shared case conversation.
              </p>
              {!data.internalHistory.items.length ? (
                <p className="dsp-muted">No operator updates yet.</p>
              ) : (
                data.internalHistory.items.map((event) => (
                  <article
                    key={event.id}
                    style={{ borderTop: '1px solid var(--dsp-border)', paddingTop: 12 }}
                  >
                    <h3 style={{ fontSize: '1rem' }}>{humanize(event.kind)}</h3>
                    <p className="dsp-muted">
                      {event.actorName} · {new Date(event.recordedAt).toLocaleString()} · Operator
                      revision {event.revision}
                    </p>
                    <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{event.body}</p>
                  </article>
                ))
              )}
              <Pagination
                page={data.internalHistory.page}
                pageSize={data.internalHistory.pageSize}
                total={data.internalHistory.total}
                totalPages={data.internalHistory.totalPages}
                onPageChange={setInternalPage}
                itemLabel="operator updates"
              />
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  operate({ action: 'internal_note', body: internalBody });
                }}
              >
                <Stack gap={12}>
                  <Field label="Private operator note" htmlFor={`${formId}-internal`}>
                    <Textarea
                      id={`${formId}-internal`}
                      required
                      minLength={3}
                      maxLength={5000}
                      rows={5}
                      value={internalBody}
                      disabled={locked}
                      onChange={(e) => setInternalBody(e.target.value)}
                    />
                  </Field>
                  <p className="dsp-muted">
                    Keep patient information, bank details and tax identifiers out of case notes.
                  </p>
                  <Button type="submit" disabled={locked || internalBody.trim().length < 3}>
                    Save private note
                  </Button>
                </Stack>
              </form>
            </Stack>
          </Card>
        </>
      )}
    </Stack>
  );
}
